import { usePostHog } from "@posthog/react";

import {
  captureSourceChooserSelected,
  type DataSourceChooserSelection,
} from "../../../lib/analytics";
import { cn } from "../../../lib/utils";
import { TierBadge, type Tier } from "../../ui/tier-badge";

export type DataSourceOption = "plaid" | "doc" | "manual";

// SILL-539: the bank card is a plain, always-selectable option. The props
// `hasPlaidEntitlement` and `onPlaidLocked` (and the locked PlusBadge they
// drove) were removed when bank verification became a Free capability.
export interface DataSourceChooserProps {
  className?: string;
  onSelect: (source: DataSourceOption) => void;
}

type DataSourceCard = {
  body: string;
  eyebrow: string;
  note?: string;
  recommended?: boolean;
  source: DataSourceOption;
  title: string;
  tier: Tier;
};

const RESULTING_TIER_BY_SOURCE: Record<
  DataSourceOption,
  DataSourceChooserSelection["resultingTier"]
> = {
  plaid: "bank-verified",
  doc: "document-backed",
  manual: "self-reported",
};

function buildCards(): readonly DataSourceCard[] {
  return [
    {
      body: "Income, balances, liabilities — verified instantly.",
      eyebrow: "Fastest",
      recommended: true,
      source: "plaid",
      tier: "bank-verified",
      title: "Connect my bank",
    },
    {
      body: "Pay stubs, W-2, 1099, statements — OCR with review.",
      eyebrow: "Document-backed",
      note: "Full-strength for 60 days.",
      source: "doc",
      tier: "document-backed",
      title: "Upload documents",
    },
    {
      body: "Keeps your readiness directional while you explore.",
      eyebrow: "Self-paced",
      source: "manual",
      tier: "self-reported",
      title: "Enter manually",
    },
  ] as const;
}

export function DataSourceChooser({
  className,
  onSelect,
}: DataSourceChooserProps) {
  const posthog = usePostHog();
  const cards = buildCards();

  const handleSelect = (source: DataSourceOption) => {
    const selection: DataSourceChooserSelection = {
      resultingTier: RESULTING_TIER_BY_SOURCE[source],
      source,
    };

    captureSourceChooserSelected(posthog.capture.bind(posthog), selection);
    onSelect(source);
  };

  return (
    <section
      aria-label="Choose how to verify"
      className={cn("space-y-3 @container", className)}
      data-slot="data-source-chooser"
    >
      <div className="space-y-1">
        <p className="eyebrow-label">Choose how to verify</p>
        <p className="text-sm text-muted-foreground">
          Pick the source that fits how much proof you want Sill to use right
          now.
        </p>
      </div>

      <div className="grid gap-3 @[42rem]:grid-cols-3">
        {cards.map((card) => (
          <button
            className={cn(
              "surface-card-interactive flex h-full w-full flex-row items-start gap-3 p-3.5 text-left",
              "@[42rem]:flex-col @[42rem]:items-start @[42rem]:gap-4 @[42rem]:p-4",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2",
              card.recommended && "border-primary/35 bg-primary/5",
            )}
            data-recommended={card.recommended ? "true" : undefined}
            data-source={card.source}
            data-source-option=""
            key={card.source}
            onClick={() => handleSelect(card.source)}
            type="button"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="eyebrow-label text-primary">
                  {card.eyebrow}
                </span>
                <TierBadge size="sm" tier={card.tier} />
              </div>
              <p className="text-sm font-semibold text-foreground sm:text-base">
                {card.title}
              </p>
              <p className="text-sm leading-6 text-muted-foreground">
                {card.body}
              </p>
              {card.note ? (
                <p className="text-xs font-medium text-muted-foreground">
                  {card.note}
                </p>
              ) : null}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
